import { getToken } from "./api";

const API_BASE: string = (import.meta.env.VITE_PUBLIC_API_URL as string) || "/api";

export interface UploadResult {
  url: string;
  name: string;
  mime_type: string;
  size_bytes: number;
}

/**
 * Upload a single file to the self-hosted backend via multipart POST.
 * Requires an authenticated session (JWT attached automatically).
 * Returns the public URL (/uploads/<file>) plus metadata.
 */
export async function uploadFile(file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append("file", file);

  const token = getToken();
  const res = await fetch(`${API_BASE}/uploads`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });

  const body = (await res.json().catch(() => null)) as {
    success?: boolean;
    message?: string;
    data?: UploadResult;
  } | null;

  if (!res.ok || !body?.success || !body.data) {
    const message = body?.message || `Upload failed (${res.status})`;
    throw new Error(message);
  }

  return body.data;
}

export function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export interface BrochureResult {
  url: string;
  name: string;
  mime_type: string;
  size_bytes: number;
  folder: string;
}

/**
 * Upload a PDF brochure. Saved to /downloads with a stable, predictable name
 * (<slug>-brochure.pdf) so download links never break between uploads.
 */
export async function uploadBrochure(file: File, slug: string): Promise<BrochureResult> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("slug", slug);

  const token = getToken();
  const res = await fetch(`${API_BASE}/uploads/brochure`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });

  const body = (await res.json().catch(() => null)) as {
    success?: boolean;
    message?: string;
    data?: BrochureResult;
  } | null;

  if (!res.ok || !body?.success || !body.data) {
    const message = body?.message || `Brochure upload failed (${res.status})`;
    throw new Error(message);
  }

  return body.data;
}